import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useQuery } from "@tanstack/react-query";
import { callPymbleApi } from "./usePymbleApi";

const EVENT_TRANSPORT_ENDPOINT = "dictionary/EventTransport";

export interface TransportOption {
	id: string;
	label: string;
	description?: string;
	imageUrl?: string;
}

/** The S2 `EventTransport` dictionary: the ways a group can travel to an event. */
export function useEventTransport() {
	const { instance } = useMsal();
	const isAuthenticated = useIsAuthenticated();

	const { data: options = [], isPending } = useQuery({
		queryKey: ["eventTransport"],
		queryFn: async (): Promise<TransportOption[]> => {
			const res = await callPymbleApi(instance, "GET", EVENT_TRANSPORT_ENDPOINT);
			if (!Array.isArray(res)) return [];
			// Entries may arrive flat or wrapped as documents with a Data array.
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const entries: any[] = res.flatMap((e: any) =>
				Array.isArray(e?.["Data"]) ? e["Data"] : [e],
			);
			return entries
				.filter((item) => item && item["Active"] !== false && item["Code"])
				.map((item) => ({
					id: String(item["Code"]),
					label: String(item["Name"] ?? item["Code"]),
					description: item["Description"] || undefined,
					imageUrl: item["ImageUrl"] || undefined,
					order: Number(item["Order"] ?? 0),
				}))
				.sort((a, b) => a.order - b.order)
				.map(({ order: _order, ...option }) => option);
		},
		enabled: isAuthenticated,
	});

	return { options, isLoading: isPending };
}
